import { computed } from '@angular/core';
import { signalStore, withComputed, withMethods, withState, patchState } from '@ngrx/signals';

import { Widget } from 'app/providers/bridge-api/dashboard.service';

export type SelectModeType =
  | 'filters'
  | 'edit'
  | 'widgetGroup'
  | 'widgetGroupBulkEdit'
  | 'comparison'
  | 'analyticItem'
  | 'goal'
  | 'dateMarker'
  | 'weight'
  | 'addFilter';

export interface SelectModeState {
  mode: SelectModeType | undefined;
  data: any;
  selected: Widget[];
  isActivityBarSelectAction: boolean;
}

const initialState: SelectModeState = {
  mode: undefined,
  data: {},
  selected: [],
  isActivityBarSelectAction: false,
};

/**
 * Signal-based store managing the widget selection mode state.
 *
 * Provided at the DashboardDetailsComponent level so every child
 * (DashboardViewComponent, WidgetComponent, etc.) shares the same instance
 * without prop-drilling via @Input / @Output.
 */
export const SelectModeStore = signalStore(
  withState(initialState),

  withComputed((state) => ({
    isActive: computed(() => state.mode() !== undefined),
    selectedCount: computed(() => state.selected().length),
    hasSelection: computed(() => state.selected().length > 0),
    filtersSourceWidgetId: computed(() =>
      state.mode() === 'filters' ? state.selected()[0]?.id : undefined
    ),
  })),

  withMethods((store) => ({
    /** Replace the entire select-mode state at once. */
    setSelectMode(
      mode: SelectModeType,
      data: any = {},
      selected: Widget[] = [],
      isActivityBarSelectAction = false
    ): void {
      patchState(store, { mode, data, selected, isActivityBarSelectAction });
    },

    /** Reset to the initial (inactive) state. */
    reset(): void {
      patchState(store, { ...initialState });
    },

    /** Toggle a widget in/out of the selected list (immutable update). */
    toggleWidget(widget: Widget): void {
      const current = store.selected();
      const exists = current.some((w) => w.id === widget.id);
      const next = exists ? current.filter((w) => w.id !== widget.id) : [...current, widget];
      patchState(store, { selected: next });
    },

    /** Replace the entire selected list. */
    setSelected(widgets: Widget[]): void {
      patchState(store, { selected: [...widgets] });
    },

    /** Check whether a specific widget is currently selected. */
    isWidgetSelected(widgetId: string): boolean {
      return store.selected().some((w) => w.id === widgetId);
    },
  }))
);
